import {useState, useEffect, useRef} from '@wordpress/element';
import {useCheckout} from '@stripe/react-stripe-js/checkout';
import {extensionCartUpdate} from '@woocommerce/blocks-checkout';

/**
 * Keeps the mounted Payment Element / Currency Selector Element in sync with the checkout
 * block's own reactive totals. The server-side session update
 * (CheckoutSessionController::maybe_sync_session_with_cart(), on woocommerce_after_calculate_totals)
 * has already run by the time cartTotal/currency here change - runServerUpdate() just tells this
 * checkout instance to re-fetch and re-render with it. Mirrors
 * UPMCheckoutSessionGateway.onUpdatedCheckout() in the classic-checkout integration.
 *
 * shouldSavePayment is the exception - nothing else pushes it to the server, so a change there
 * is pushed via the Store API cart/extensions endpoint (CheckoutSessionController::update_save_payment_method(),
 * this gateway's registered update callback) before runServerUpdate() runs, or it would just
 * re-fetch stale state.
 *
 * Returns `syncing` - true only while extensionCartUpdate (our server-side request) is in
 * flight, not runServerUpdate() (a client-side Stripe.js re-fetch, not one of ours).
 *
 * @param {string}  gatewayId
 * @param {Object}  cartTotal
 * @param {Object}  currency
 * @param {boolean} shouldSavePayment
 *
 * @returns {{syncing: boolean}}
 */
export const useSyncCheckoutSession = ({gatewayId, cartTotal, currency, shouldSavePayment}) => {
    const checkoutState = useCheckout();
    const checkoutStateRef = useRef(checkoutState);
    checkoutStateRef.current = checkoutState;

    const [syncing, setSyncing] = useState(false);

    const signature = `${currency?.code}:${cartTotal?.value}:${shouldSavePayment ? 1 : 0}`;

    // The session was created server-side with the current totals/checkbox state - seed the
    // baseline from the first render so the initial mount doesn't trigger a redundant sync.
    const lastSyncedSignature = useRef(signature);
    const lastSyncedSavePayment = useRef(shouldSavePayment);

    useEffect(() => {
        if (signature === lastSyncedSignature.current) {
            return;
        }
        lastSyncedSignature.current = signature;

        const savePaymentChanged = shouldSavePayment !== lastSyncedSavePayment.current;
        lastSyncedSavePayment.current = shouldSavePayment;

        let pushUpdate;
        if (savePaymentChanged) {
            setSyncing(true);
            pushUpdate = extensionCartUpdate({namespace: gatewayId, data: {shouldSavePayment}})
                .finally(() => setSyncing(false));
        } else {
            pushUpdate = Promise.resolve();
        }

        pushUpdate
            .then(() => {
                const checkoutState = checkoutStateRef.current;
                return checkoutState.type === 'success'
                    ? checkoutState.checkout.runServerUpdate(() => Promise.resolve())
                    : null;
            })
            .then((result) => {
                if (result?.type === 'error') {
                    console.error('Adaptive Pricing runServerUpdate error:', result.error);
                }
            })
            .catch((error) => {
                console.error('Adaptive Pricing runServerUpdate failed:', error);
            });
    }, [gatewayId, signature, shouldSavePayment]);

    return {syncing};
};